<template>
  <div v-if="results.length !== 0" :class="$style.container">
    <div :class="$style.title">
      {{
        translations?.['global.search.filterResults'] ||
        translations?.['filterapp.stage-filter-cta']
      }}
    </div>

    <div ref="topAnchor" :class="$style.topAnchor" />

    <div :class="$style.filter">
      <Tag
        v-for="(filter, index) in filters"
        :key="index"
        :is-active="currentFilter === filter.name"
        size="s"
        :text="`${capitalize(filter.label)} (${filter.hits})`"
        @click="startNewSearch(filter.name)"
      />
    </div>

    <Button1XP
      v-if="currentFilter"
      size="xs"
      start-icon="close"
      :text="filterResetText"
      variant="link"
      @click="scrollToAndReset"
    />

    <div ref="bottomAnchor" :class="$style.bottomAnchor" />
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { checkIsMobile } from '../../utils/media';
import Tag from '../Tag/Tag.vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { post } from '../../xhr/post';
import { useStoreTranslations } from '../../store/translations/translations';

const emit = defineEmits(['filtered']);

const props = defineProps({
  filterResetText: {
    type: String,
    default: '',
  },
  results: {
    type: Array,
    default: () => [],
  },
  filters: {
    type: Array,
    default: () => [],
  },
  terms: {
    type: Array,
    default: () => [],
  },
  rangeSize: {
    type: Number,
  },
});

const storeConfiguration = useStoreConfiguration();
const storeTranslation = useStoreTranslations();

const currentFilter = ref(null);
const filteredResults = ref([]);
const useCategorySearch = ref(false);
const range = ref({
  from: 0,
});
const loading = ref(false);
const totalReached = ref(false);
const isMobile = ref(false);

const disableExternalDocuments = window.disableExternalDocuments;
const computedCountry = computed(() => {
  return (
    storeConfiguration.caasConfig?.searchCountry ??
    window?.searchCountry ??
    storeConfiguration.caasConfig?.currentCountry ??
    window?.currentCountry ??
    storeConfiguration.caasConfig?.locale
  );
});

const bottomAnchor = ref(null);
const topAnchor = ref(null);

const translations = ref(null);

const capitalize = (value) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const checkScreenSize = () => (isMobile.value = checkIsMobile());

const initScroll = () => {
  const distanceToStartLoad = 300;

  window.onscroll = () => {
    let bottomOfWindow =
      document.documentElement.clientHeight +
        document.documentElement.scrollTop +
        distanceToStartLoad >=
      document.documentElement.scrollHeight;

    if (bottomOfWindow && !loading.value && useCategorySearch.value && !totalReached.value) {
      range.value.from = range.value.from + props.rangeSize;
      getSearchResults(currentFilter.value, true);
    }
  };
};

const startNewSearch = (category) => {
  if (currentFilter.value === category) {
    return;
  }
  range.value.from = 0;
  useCategorySearch.value = true;
  totalReached.value = false;

  checkScreenSize();

  if (isMobile.value) {
    bottomAnchor.value?.scrollIntoView({ behavior: 'smooth' });
  }

  getSearchResults(category);
};

const getSearchResults = (category, onScroll) => {
  loading.value = true;
  currentFilter.value = category;

  post(
    `${storeConfiguration.config?.pt_search_base_url}${storeConfiguration.config?.caas_categorySearchUrl}`,
    {
      // global
      container: storeConfiguration.config?.pt_search_container,
      shopContainer: storeConfiguration.config?.pt_search_shop_container,
      country: computedCountry.value,
      disableExternalDocuments,
      language: storeConfiguration.caasConfig.searchCountry
        ? storeConfiguration.caasConfig.language.replace('EN-US', 'EN')
        : storeConfiguration.caasConfig.language,
      instance: storeConfiguration.caasConfig.instance || window.instance,
      // payload
      categoryUid: category,
      from: range.value.from,
      size: props.rangeSize,
      word: props.terms,
    },
  )
    .then((responseData) => {
      if (onScroll) {
        filterResults(responseData, true);
      } else {
        filterResults(responseData);
      }
    })
    .catch((error) => console.error(error));
};

const filterResults = (data, update) => {
  if (props.filters.length > 1) {
    if (update) {
      filteredResults.value.push(...data.hits.hits);
    } else {
      filteredResults.value = data.hits.hits;
      initScroll();
    }

    loading.value = false;
    for (const filter of props.filters) {
      if (currentFilter.value === filter.name && filteredResults.value.length === filter.hits) {
        totalReached.value = true;
      }
    }
  }
};

const scrollToAndReset = () => {
  checkScreenSize();

  if (isMobile.value) {
    topAnchor.value?.scrollIntoView({ behavior: 'smooth' });
  }

  resetFilters();
};

const resetFilters = () => {
  currentFilter.value = null;
  filteredResults.value = props.results;
  useCategorySearch.value = false;
  emit('filtered', filteredResults.value);
};

watch(
  () => props.terms,
  (newTerms, oldTerms) => {
    if (newTerms !== oldTerms) {
      resetFilters();
    }
  },
);

watch(filteredResults, (newValue, oldValue) => {
  if (newValue !== oldValue && newValue.length) {
    emit('filtered', filteredResults.value);
  }
});

watch(
  () => storeTranslation.translations,
  (newValue) => {
    if (newValue) {
      translations.value = newValue;
    }
  },
  { immediate: true },
);
</script>

<style module lang="scss">
@forward './ResultFilter.scss';
</style>
